import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {doesPublicFileExistAsync} from '@Utils/file.util';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {RemoveVideoBackgroundResponse, RemoveVideoBackgroundTrimData} from '@Utils/video.util';
import {
  getEstimatedTimeForBackgroundRemoval,
  pollVideoBackgroundRemovalStatus,
  removeUserVideoBackground,
  removeVideoBackground,
  removeVideoBackgroundV2,
  resumeVideoBackgroundRemovalV2,
} from '@Utils/video.util';
import type {PollKickoffAck} from '@Libraries/pollable-job.library.types';
import {PollError, PollFailError} from '@Libraries/pollable-job.library.types';
import type {TrimData} from '@Panels/trim-panels/trim-panels.types';
import {getRemovedBgFilenameWithExtension, getRepoVideoURL} from '@Libraries/user-video-library';
import {ItemLoadingProgressType, ItemLoadingReason} from '@PosterWhiteboard/items/item/item.types';
import {secondsToMilliseconds, toMicrosecondIntegralSeconds} from '@Utils/math.util';
import {isPremiumUser} from '@Utils/user.util';
import {startItemLoading, removeItemLoading} from '@Libraries/item-loading.library';

const ESTIMATED_OVERHEAD_TIME = 180;
const LEGACY_INSUFFICIENT_CREDITS_ERROR_CODE = 'insufficient_credits';
const V2_INSUFFICIENT_CREDITS_ERROR_CODE = 'INSUFFICIENT_CREDITS';
const V2_EXPIRED_ERROR_CODE = 'EXPIRED';

export interface RemoveVideoItemBackgroundObject {
  isBackgroundRemoved: boolean;
  isBackgroundBeingRemoved: boolean;
  trimData: TrimData;
  // eslint-disable-next-line no-restricted-syntax -- transient in-flight poll key; poster-history strips it from snapshots (and deletes require an optional), so diff-based undo never restores or clears it
  pollingKey?: string;
}

function isErrorWithCode(e: unknown, code: string): boolean {
  return typeof e === 'object' && e !== null && 'code' in e && e.code === code;
}

export class RemoveVideoItemBackground extends CommonMethods {
  public isBackgroundRemoved = false;
  public isBackgroundBeingRemoved = false;
  public isBackgroundRemovalPollingInitialized = false;
  public trimData: TrimData = {
    startTime: 0,
    endTime: 0,
  };
  public pollingKey?: string;

  #item: VideoItem | VideoSlideItem;

  constructor(item: VideoItem | VideoSlideItem) {
    super();
    this.#item = item;
  }

  public toObject(): RemoveVideoItemBackgroundObject {
    return {
      isBackgroundRemoved: this.isBackgroundRemoved,
      isBackgroundBeingRemoved: this.isBackgroundBeingRemoved,
      trimData: this.trimData,
      pollingKey: this.pollingKey,
    };
  }

  public clearInProgressState(): void {
    this.isBackgroundBeingRemoved = false;
    this.isBackgroundRemovalPollingInitialized = false;
    this.pollingKey = undefined;
  }

  public async ensureRemovedBackgroundVideoExistsIfNeeded(): Promise<void> {
    if (this.isBackgroundRemoved && !(await this.doesRemovedBackgroundFileExist())) {
      await removeVideoBackground(this.#item.hashedFilename, this.#item.removeBackground.trimData.startTime, this.#item.removeBackground.trimData.endTime);
    }
  }

  public async doesRemovedBackgroundFileExist(): Promise<boolean> {
    const removedBgFilename = getRemovedBgFilenameWithExtension(
      this.#item.hashedFilename,
      this.#item.removeBackground.trimData.startTime,
      this.#item.removeBackground.trimData.endTime
    );
    const destURL = getRepoVideoURL(removedBgFilename, this.#item.page.poster.mode.isGeneration());
    return doesPublicFileExistAsync(destURL);
  }

  public async removeBackground(): Promise<void> {
    if (this.isBackgroundBeingRemoved) {
      return;
    }

    this.isBackgroundBeingRemoved = true;
    this.isBackgroundRemovalPollingInitialized = true;
    this.#item.page.poster.redux.updateReduxData();
    try {
      const estimatedBackgroundRemovalTime = await getEstimatedTimeForBackgroundRemoval(
        this.#item.hashedFilename,
        this.#item.getStartTimeForNonRemovedBgVideo(),
        this.#item.getEndTimeForNonRemovedBgVideo()
      );
      startItemLoading(this.#item, {
        reason: ItemLoadingReason.AI_REMOVE_BACKGROUND,
        langTextKeys: ['pmwjs_removing_background_percentage', 'pmwjs_removing_bg_percentage'],
        progress: {
          type: ItemLoadingProgressType.FAKE,
          expectedTotalTime: estimatedBackgroundRemovalTime,
        },
      });

      const creditsToCharge: number = await this.getCreditsToChargeForBackgroundRemovalForUser();
      if ((await this.doesRemovedBackgroundFileExist()) && creditsToCharge === 0) {
        if (this.isBackgroundRemoved) {
          return;
        }

        await this.#item.enableRemoveBackgroundFlag(this.#getTrimDataForExistingFile());
        return;
      }

      const estimatedAjaxTimeoutInMilliseconds = secondsToMilliseconds(estimatedBackgroundRemovalTime + ESTIMATED_OVERHEAD_TIME);
      const response = await this.#requestBackgroundRemoval(estimatedAjaxTimeoutInMilliseconds);

      if (!this.#item.existsInPage()) {
        return;
      }

      await this.#applyRemovalResult(response);
    } catch (e) {
      this.#onRemoveBackgroundError(e);
    } finally {
      this.#resetRemoveBackgroundState();
    }
  }

  /**
   * The only route into removeBackground.trimData that skips the backend (which returns
   * micro-integral seconds), so it must snap or the poster keeps raw item times that no
   * filename builder can reproduce.
   */
  #getTrimDataForExistingFile(): RemoveVideoBackgroundTrimData {
    return {
      trimStart: toMicrosecondIntegralSeconds(this.#item.startTime),
      trimEnd: toMicrosecondIntegralSeconds(this.#item.endTime),
    };
  }

  public async getCreditsToChargeForBackgroundRemovalForUser(): Promise<number> {
    return getPosterUi().getCreditsCostForVideoBackgroundRemoval(this.#item.hashedFilename, this.#item.getStartTimeForNonRemovedBgVideo(), this.#item.getEndTimeForNonRemovedBgVideo());
  }

  public resumeBackgroundRemovalIfNeeded(): void {
    if (this.isBackgroundBeingRemoved && !this.isBackgroundRemoved && !this.isBackgroundRemovalPollingInitialized) {
      this.isBackgroundRemovalPollingInitialized = true;
      void this.#resumeRemoveBackgroundRequest();
    }
  }

  /**
   * Owns the streamGatewayEnabled fork for a fresh kickoff. The V2 branch persists
   * the ack's pollingKey via onAck as soon as the job is claimed, before the (long)
   * observation completes, so a poster reload during processing can resume by key.
   */
  async #requestBackgroundRemoval(estimatedAjaxTimeoutInMilliseconds: number): Promise<RemoveVideoBackgroundResponse> {
    if (window.PMW.streamGatewayEnabled) {
      return removeVideoBackgroundV2(this.#item.hashedFilename, this.#item.startTime, this.#item.endTime, {
        timeoutMs: estimatedAjaxTimeoutInMilliseconds,
        onAck: (ack: PollKickoffAck): void => {
          this.pollingKey = ack.pollingKey;
        },
      });
    }

    this.pollingKey = await removeUserVideoBackground(this.#item.hashedFilename, this.#item.startTime, this.#item.endTime);
    return pollVideoBackgroundRemovalStatus(this.pollingKey, estimatedAjaxTimeoutInMilliseconds);
  }

  /**
   * Observe-only: never kicks off a new job. On the V2 path a missing pollingKey
   * (old-format or already-cleared poster state) resets silently, and a PollError
   * with code EXPIRED (job long gone) also resets silently with no error modal.
   */
  async #resumeRemoveBackgroundRequest(): Promise<void> {
    if (!window.PMW.streamGatewayEnabled) {
      await this.#resumeRemoveBackgroundLegacy();
      return;
    }

    if (!this.pollingKey) {
      this.#resetRemoveBackgroundState();
      return;
    }

    try {
      const response = await resumeVideoBackgroundRemovalV2(this.pollingKey);

      if (!this.#item.existsInPage()) {
        return;
      }

      await this.#applyRemovalResult(response);
    } catch (e) {
      if (e instanceof PollError && e.code === V2_EXPIRED_ERROR_CODE) {
        return;
      }
      this.#onRemoveBackgroundError(e);
    } finally {
      this.#resetRemoveBackgroundState();
    }
  }

  async #resumeRemoveBackgroundLegacy(): Promise<void> {
    if (!this.pollingKey) {
      return;
    }

    try {
      const estimatedBackgroundRemovalTime = await getEstimatedTimeForBackgroundRemoval(
        this.#item.hashedFilename,
        this.#item.getStartTimeForNonRemovedBgVideo(),
        this.#item.getEndTimeForNonRemovedBgVideo()
      );
      const estimatedAjaxTimeoutInMilliseconds = secondsToMilliseconds(estimatedBackgroundRemovalTime + ESTIMATED_OVERHEAD_TIME);
      const response = await pollVideoBackgroundRemovalStatus(this.pollingKey, estimatedAjaxTimeoutInMilliseconds);

      if (!this.#item.existsInPage()) {
        return;
      }

      await this.#applyRemovalResult(response);
    } catch (e) {
      this.#onRemoveBackgroundError(e);
    } finally {
      this.#resetRemoveBackgroundState();
    }
  }

  /** Success handling shared by a fresh kickoff and a resumed observation. */
  async #applyRemovalResult(response: RemoveVideoBackgroundResponse): Promise<void> {
    if (response.remainingRemoveBackgroundCredits === undefined) {
      return;
    }

    getPosterUi().updateVideoBgRemovalCredits(this.#item.hashedFilename, this.#item.startTime, this.#item.endTime, 0);
    void getPosterUi().refreshTeamCreditsInStore();

    await this.#item.enableRemoveBackgroundFlag(response.trimData);
  }

  #onInSufficientFunds(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const cb = (credits: number): void => {
        getPosterUi().onCreditsPurchaseSuccess(this.#item, credits);
        resolve();
      };

      this.getCreditsToChargeForBackgroundRemovalForUser()
        .then((creditsToCharge) => {
          const remainingCredits = window.PMW.redux.store.getState().teamCredits.credits;

          getPosterUi().triggerBuyMoreCredits({
            headingText: window.i18next.t('pmwjs_credits_pack_on_background_removal', {creditsRemaining: remainingCredits, creditsCost: creditsToCharge}),
            modalTitle: window.i18next.t('pmwjs_youre_out_of_credits'),
            cb,
          });
        })
        .catch((e: unknown) => {
          console.error(e);
          reject(e instanceof Error ? e : new Error('RemoveVideoItemBackground: failed to resolve credits to charge'));
        });
    });
  }

  #isInsufficientCreditsError(e: unknown): boolean {
    if (e instanceof PollFailError) {
      return e.code === V2_INSUFFICIENT_CREDITS_ERROR_CODE;
    }
    return isErrorWithCode(e, LEGACY_INSUFFICIENT_CREDITS_ERROR_CODE);
  }

  #onRemoveBackgroundError(e: unknown): void {
    if (!this.#item.existsInPage()) {
      return;
    }

    console.error(e);

    if (this.#isInsufficientCreditsError(e)) {
      if (!isPremiumUser()) {
        void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS);
      } else {
        void this.#onInSufficientFunds();
      }
      return;
    }

    getPosterUi().openMessageModal({
      title: window.i18next.t('pmwjs_remove_video_bg_failed'),
      text: window.i18next.t('pmwjs_remove_video_bg_failed_desc'),
    });
  }

  #resetRemoveBackgroundState(): void {
    if (!this.#item.existsInPage()) {
      return;
    }

    this.clearInProgressState();
    removeItemLoading(this.#item);
    this.#item.page.poster.redux.updateReduxData();
  }
}
